import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

const DEFAULT_CALENDLY_URL = 'https://calendly.com/nkamboh-dejavunomad/30min';
const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const DEFAULT_MIN_HEIGHT = 980;

/**
 * Loads Calendly's official inline embed for the configured scheduling URL.
 * Optional `utm` is passed into Calendly.initInlineWidget for attribution.
 * Optional `onEventScheduled` fires when the invitee books.
 * Height grows with Calendly's page_height messages so steps like "Enter Details"
 * are fully visible without clipping the Schedule button or forcing a panel scroll.
 */
const CalendlyEmbed = ({
  url = process.env.REACT_APP_CALENDLY_URL || DEFAULT_CALENDLY_URL,
  minHeight = DEFAULT_MIN_HEIGHT,
  title,
  prefill = {},
  utm = {},
  onEventScheduled,
}) => {
  const containerRef = useRef(null);
  const onScheduledRef = useRef(onEventScheduled);
  onScheduledRef.current = onEventScheduled;
  const [widgetHeight, setWidgetHeight] = useState(minHeight);

  useEffect(() => {
    setWidgetHeight((prev) => Math.max(prev, minHeight));
  }, [minHeight]);

  useEffect(() => {
    if (!url) return undefined;

    const initWidget = () => {
      if (window.Calendly && containerRef.current) {
        containerRef.current.innerHTML = '';
        window.Calendly.initInlineWidget({
          url,
          parentElement: containerRef.current,
          prefill: {
            name: prefill.name || '',
            email: prefill.email || '',
          },
          utm: {
            utmSource: utm.utmSource || '',
            utmMedium: utm.utmMedium || '',
            utmCampaign: utm.utmCampaign || '',
            utmContent: utm.utmContent || '',
            utmTerm: utm.utmTerm || '',
          },
        });
      }
    };

    const existingScript = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
    if (existingScript) {
      if (window.Calendly) {
        initWidget();
      } else {
        existingScript.addEventListener('load', initWidget);
        return () => existingScript.removeEventListener('load', initWidget);
      }
      return undefined;
    }

    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT_SRC;
    script.async = true;
    script.onload = initWidget;
    document.body.appendChild(script);

    return undefined;
  }, [
    url,
    prefill.name,
    prefill.email,
    utm.utmSource,
    utm.utmMedium,
    utm.utmCampaign,
    utm.utmContent,
    utm.utmTerm,
  ]);

  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== 'https://calendly.com') return;
      const payload = event.data;
      if (!payload || typeof payload !== 'object') return;

      if (payload.event === 'calendly.page_height') {
        const nextHeight = Number.parseInt(payload.payload?.height, 10);
        if (Number.isFinite(nextHeight) && nextHeight > 0) {
          // Extra padding so the Schedule button and footer aren't clipped.
          setWidgetHeight(Math.max(minHeight, nextHeight + 24));
        }
        return;
      }

      if (payload.event !== 'calendly.event_scheduled') return;
      const invitee = payload.payload?.invitee || {};
      const scheduledEvent = payload.payload?.event || {};
      const questionsAndAnswers = invitee.questions_and_answers || [];
      const inviteeNotes = questionsAndAnswers
        .map((qa) => {
          const question = String(qa?.question || '').trim();
          const answer = String(qa?.answer || '').trim();
          if (!question && !answer) return '';
          return `${question}: ${answer}`.trim();
        })
        .filter(Boolean)
        .join('\n') || null;
      onScheduledRef.current?.({
        email: invitee.email,
        name: invitee.name,
        scheduledAt: scheduledEvent.start_time || invitee?.scheduled_event?.start_time,
        timezone: invitee.timezone,
        uri: invitee.uri,
        inviteeNotes,
      });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [minHeight]);

  if (!url) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          {title || 'Demo scheduling is temporarily unavailable.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className="calendly-inline-widget"
      data-testid="calendly-inline-widget"
      data-url={url}
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        height: widgetHeight,
        minHeight: { xs: Math.max(860, minHeight - 40), md: minHeight },
        width: '100%',
        borderRadius: 2,
        overflow: 'visible',
        backgroundColor: '#fff',
        '& iframe': {
          maxWidth: '100% !important',
          width: '100% !important',
          height: `${widgetHeight}px !important`,
          minHeight: `${widgetHeight}px !important`,
        },
      }}
    />
  );
};

export default CalendlyEmbed;
export { DEFAULT_CALENDLY_URL };
